/**
 * Sticky banner on every operator screen warning when Stripe Connect
 * isn't fully onboarded yet. Without Connect-ready, the public rental
 * flow refuses reservations (public_rental.py returns 503), so this is
 * the operator's most-important next action.
 *
 * Self-hides when `connect.ready` is true.
 */
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { connect, self } from "./api";
import { Btn } from "./Btn";
import { Icon } from "./Icon";

export function ConnectBanner() {
  // Only render once we know the operator is signed in.
  const me = useQuery({
    queryKey: ["auth", "me"],
    queryFn: () => self.me(),
    retry: false,
    staleTime: 5 * 60_000,
  });
  const status = useQuery({
    queryKey: ["connect", "me"],
    queryFn: () => connect.me(),
    enabled: me.isSuccess,
    retry: false,
  });

  if (!status.data) return null;
  if (status.data.ready) return null;

  const submitted = status.data.details_submitted;
  const hasAccount = !!status.data.stripe_account_id;

  let text: string;
  let cta: string;
  if (!hasAccount) {
    text =
      "Tenant rent isn't going anywhere yet — connect a Stripe account to start accepting online payments.";
    cta = "Connect Stripe";
  } else if (!submitted) {
    text =
      "Stripe needs a few more details before you can take rent. Finish onboarding to enable online rentals.";
    cta = "Resume Stripe setup";
  } else {
    text =
      "Stripe is reviewing your account — outstanding requirements need attention before payouts can run.";
    cta = "View requirements";
  }

  return (
    <div className="flex items-center gap-3 px-5 lg:px-7 py-2 bg-warn-soft text-warn text-[13px]">
      <Icon name="warning" size={14} className="shrink-0" />
      <span className="flex-1 font-medium">{text}</span>
      <Link to="/app/settings/payouts">
        <Btn kind="primary" size="sm">
          {cta}
        </Btn>
      </Link>
    </div>
  );
}
